import React, { useState, useEffect, Suspense } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, CircularProgress } from '@mui/material';
import theme from './theme';
import { getSession, clearSession } from './utils/storage';
import Login from './components/auth/Login';
import AdminDashboard from './components/admin/AdminDashboard';
import CandidateDashboard from './components/candidate/CandidateDashboard';
import ViewerDashboard from './components/viewer/ViewerDashboard';
import './i18n';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check for existing session on mount
  useEffect(() => {
    const session = getSession();
    if (session?.role === 'admin' && !session.access_token) {
      clearSession();
    } else if (session) {
      setUser(session);
    }
    setLoading(false);
    const onAuthExpired = () => setUser(null);
    window.addEventListener('auth-expired', onAuthExpired);
    return () => window.removeEventListener('auth-expired', onAuthExpired);
  }, []);

  const handleLoginSuccess = (userData) => {
    setUser(userData);
  };

  const handleLogout = () => {
    clearSession();
    setUser(null);
  };

  // Show loading state
  if (loading) {
    return null;
  }

  // No session - show login
  if (!user) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Suspense fallback={
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
            <CircularProgress />
          </Box>
        }>
          <Login onLoginSuccess={handleLoginSuccess} />
        </Suspense>
      </ThemeProvider>
    );
  }

  // Render based on role
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Suspense fallback={
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
          <CircularProgress />
        </Box>
      }>
        {user.role === 'admin' ? (
          <AdminDashboard user={user} onLogout={handleLogout} />
        ) : user.role === 'viewer' ? (
          <ViewerDashboard user={user} onLogout={handleLogout} />
        ) : (
          <CandidateDashboard user={user} onLogout={handleLogout} />
        )}
      </Suspense>
    </ThemeProvider>
  );
}

export default App;
